import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  FlaskConical,
  ShieldCheck,
  Eye,
  Gauge,
  Microscope,
  HandCoins,
  CheckCircle2,
} from "lucide-react";
import { BOOKING_URL } from "@/lib/api";
import {
  Container,
  Section,
  Eyebrow,
  Reveal,
  MaskedLines,
  GlowOrb,
  goldBtn,
  ghostBtn,
} from "@/components/kit";
import { CtaBand } from "@/components/CtaBand";

const chapters = [
  {
    n: "01",
    title: "We're a lab, not an agency",
    body: "Agencies sell hours and hope. We run experiments. Every campaign is a hypothesis — tested, measured, and iterated until qualified meetings become predictable. If a play doesn't produce pipeline, we kill it and move on.",
  },
  {
    n: "02",
    title: "Outcomes are the only currency",
    body: "We refuse to hide behind vanity metrics. Opens and clicks don't pay salaries — qualified, attended meetings do. That's why we tie our model to the outcome you actually care about, not the activity that's easy to invoice.",
  },
  {
    n: "03",
    title: "Precision beats volume",
    body: "Anyone can blast ten thousand emails. Booking the right forty meetings with perfect-fit buyers takes research, scoring and restraint. We'd rather send fewer, sharper messages to accounts that match your ICP exactly.",
  },
  {
    n: "04",
    title: "Transparency, by default",
    body: "You see the parameters, the scoring, the sequences and the results. No black boxes, no inflated dashboards. When something works we tell you why; when it doesn't, we tell you that too — then fix it.",
  },
];

const philosophy = [
  { icon: Microscope, title: "Research-first", desc: "Nothing ships until the ICP, signals and messaging are grounded in real data." },
  { icon: Gauge, title: "Performance-tied", desc: "Our incentives are aligned to your pipeline, not our retainer." },
  { icon: Eye, title: "Radically transparent", desc: "Full visibility into targeting, sequences, and every booked meeting." },
  { icon: FlaskConical, title: "Experimental", desc: "Constant testing of angles, channels and offers to compound results." },
  { icon: HandCoins, title: "Pay-for-performance", desc: "You pay for qualified outcomes — never for activity or no-shows." },
  { icon: ShieldCheck, title: "Quality-obsessed", desc: "Deliverability and ICP-match protected at every single step." },
];

const standards = [
  "We never bill for unqualified or no-show meetings.",
  "Every account is scored against your ICP before outreach.",
  "We own and protect sending infrastructure and deliverability.",
  "No misleading personalization or fake mutual connections.",
  "Parameters are agreed in writing before a campaign launches.",
  "Reporting reflects reality — qualified meetings, nothing inflated.",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-24">
        <GlowOrb className="right-[-4%] top-[8%] h-[520px] w-[520px] opacity-80" />
        <div className="absolute inset-0 -z-10 grain opacity-[0.035]" />
        <Container className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <Eyebrow>The Manifesto</Eyebrow>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.98] text-white">
              <MaskedLines lines={["An outbound", "growth lab —"]} delay={0.1} />
              <MaskedLines
                lines={["not an agency"]}
                lineClassName="text-gold text-glow-gold"
                delay={0.1 + 2 * 0.11}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-white/65"
            >
              ICP Matters was founded on a simple frustration: the outbound industry sells activity
              and calls it results. We built a lab to prove there's a better way — one where you
              only pay for meetings that actually move your business forward.
            </motion.p>
          </div>

          <Reveal delay={0.2} className="relative">
            <div className="pointer-events-none absolute inset-0 gold-radial opacity-60 blur-xl" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/ec31a5d1-9734-408d-b99e-f684b9731cbb/images/8f95f0c7d34197343b1421905ddfea0462a1033e41314c1460f83b535a95c690.jpeg"
                alt="Futuristic ICP targeting — locking onto the ideal customer within a network of accounts"
                className="h-[420px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/10 bg-navy-900/70 backdrop-blur px-5 py-4">
                <p className="font-mono-jb text-[11px] uppercase tracking-widest text-white/45">
                  Founder-led · Since inception
                </p>
                <p className="mt-1 text-sm text-white/80">
                  "We only win when you get qualified pipeline."
                </p>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Numbered chapters */}
      <Section data-testid="manifesto-chapters" className="border-y border-white/10 bg-navy-800/30">
        <Container>
          <div className="space-y-14 md:space-y-16">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="grid gap-6 md:grid-cols-[auto_1fr] md:gap-12">
                  <span className="font-mono-jb text-6xl md:text-8xl font-semibold leading-none text-gold/25">
                    {c.n}
                  </span>
                  <div className="max-w-3xl">
                    <h2 className="font-display text-2xl md:text-4xl font-medium tracking-tight text-white">
                      {c.title}
                    </h2>
                    <p className="mt-4 text-white/60 text-base md:text-lg leading-relaxed">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* Philosophy grid */}
      <Section data-testid="philosophy-grid">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Core Philosophy</Eyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              The principles behind every campaign
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {philosophy.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-2xl border border-white/10 bg-navy-800 p-8 transition-[border-color,transform] duration-300 hover:-translate-y-1.5 hover:border-gold/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 border border-gold/25 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy-900">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/55">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* Lab standards / non-negotiables */}
      <Section data-testid="lab-standards" className="border-t border-white/10 bg-navy-800/30">
        <Container className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <div>
            <Eyebrow>Lab Standards</Eyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              Our non-negotiables
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              These aren't marketing lines — they're the operating rules we hold ourselves to on
              every engagement.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className={goldBtn}>
                Book A Discovery Call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link to="/contact" className={ghostBtn}>
                Get Qualified Meetings
              </Link>
            </div>
          </div>

          <div className="grid gap-4">
            {standards.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-navy-900 p-5 transition-colors duration-300 hover:border-gold/40">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <p className="text-white/75">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <CtaBand />
    </div>
  );
}
